import { createSelector } from 'reselect'
import getMerchandiseShippingItems from '../items/getMerchandiseShippingItems'
import getMerchandiseItems from '../items/getMerchandiseItems'
import { groupBy } from 'lib/array/arrayUtils'
import { getCurrentIntl } from 'i18n/util/currentIntl'

const getMerchandiseShippingGroups = createSelector(
  (state: App.State) => state.merchandise.merchandiseShippingQuotes,
  (state: App.State) => state.merchandise.fetchingMerchandiseShippingQuotes,
  (state: App.State) => state.merchandise.merchandiseShippingQuotesError,
  (quotes, fetching, error): App.WithDataStatus<App.MerchandiseShippingQuotesResponse> => {
    return {
      hasRequiredData: !fetching && !!quotes && !error,
      data: quotes || { shippingGroups: [] },
    }
  },
)

const getMerchandiseShippingItemViews = createSelector(
  (state: App.State) => getMerchandiseShippingItems(state),
  (state: App.State) => getMerchandiseItems(state),
  (state: App.State) => getMerchandiseShippingGroups(state),
  (shippingItems, merchandiseItems, shippingGroups): App.WithDataStatus<Array<App.Checkout.MerchandiseShippingItemView>> => {
    // If no merchandise items, no shipping quotes needed
    if (shippingItems.length === 0) {
      return {
        hasRequiredData: true,
        data: [],
      }
    }

    if (!shippingGroups.hasRequiredData) {
      return {
        hasRequiredData: false,
        data: [],
      }
    }

    const merchandiseItemsByShippingId = groupBy(merchandiseItems, item => item.shippingId)
    const intl = getCurrentIntl()

    return {
      hasRequiredData: true,
      data: shippingItems.map((item): App.Checkout.MerchandiseShippingItemView => {
        const itemsInThisGroup = merchandiseItemsByShippingId.get(item.shippingId) ?? []
        const itemIds = itemsInThisGroup.map(i => i.itemId)

        const shippingGroup = shippingGroups.data.shippingGroups.find(group =>
          group.items.some(groupItem => itemIds.includes(groupItem.itemId)),
        )

        const deliveryFee = shippingGroup?.shippingCost ?? 0
        const deliveryGst = shippingGroup?.shippingGst ?? 0

        // Use server-provided shippingGroupId instead of client-generated ID
        return {
          title: intl.formatMessage({
            id: 'w-zmSLfM2wEt-0b0fqUk',
            defaultMessage: 'Delivery fee',
            description: 'Title for merchandise delivery fee item in checkout',
          }),
          item: {
            ...item,
            itemId: shippingGroup?.shippingGroupId ?? item.itemId,
            transactionKey: shippingGroup?.shippingGroupId ?? item.transactionKey,
          },
          loyalty: {
            productType: 'shop_shipping',
          },
          totals: {
            price: 0,
            memberPrice: 0,
            value: 0,
            memberValue: 0,
            surcharge: 0,
            extraGuestSurcharge: 0,
            taxesAndFees: 0,
            otherFees: {
              deliveryFee,
              deliveryGst,
            },
          },
        }
      }),
    }
  },
)

export default getMerchandiseShippingItemViews
